import { onUnmounted, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';

/**
 * Shared loader for `server-detail.html`'s three introspection tabs
 * (Scheduled tasks / Security / Domains & vhosts — server-introspection
 * slice, 2026-07-19).
 *
 * `load()` reads the last-known result from
 * `GET {apiBase}/{serverId}/introspection/{command}` (persisted per
 * server+command by the observability module), so a tab renders instantly
 * with a fetched-at timestamp. `refresh()` re-issues the command through the
 * EXISTING commands endpoint (`POST {apiBase}/{serverId}/commands`) — the
 * result lands back asynchronously via the hub's `command.result` webhook —
 * then polls the introspection endpoint until `collected_at` advances (or
 * gives up: the agent may be offline, or still on a pre-3.0.23-staging.11
 * build that rejects these commands as `unknown command type`).
 */
export default function useIntrospection(apiBase, serverId, command) {
  const api = useApi();

  const data = ref(null);
  const collectedAt = ref(null);
  const loaded = ref(false);
  const refreshing = ref(false);
  const error = ref(null);

  let pollTimer = null;
  onUnmounted(() => clearTimeout(pollTimer));

  async function load() {
    try {
      const res = await api.get(`${apiBase}/${serverId}/introspection/${command}`);
      data.value = res.data ?? null;
      collectedAt.value = res.collected_at ?? null;
    } catch {
      // Empty state renders either way (matches the other tabs' loaders).
    } finally {
      loaded.value = true;
    }
  }

  async function refresh() {
    if (refreshing.value) {
      return;
    }
    refreshing.value = true;
    error.value = null;
    const before = collectedAt.value;

    try {
      await api.post(`${apiBase}/${serverId}/commands`, { command, payload: {} });
    } catch (err) {
      error.value = err?.response?.data?.error || 'Could not issue the command.';
      refreshing.value = false;
      return;
    }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      await load();
      if (collectedAt.value !== null && collectedAt.value !== before) {
        refreshing.value = false;
        return;
      }
      if (attempts >= 10) {
        refreshing.value = false;
        error.value = 'No fresh result yet — the agent may be offline or not support this command yet.';
        return;
      }
      pollTimer = setTimeout(poll, 3000);
    };
    pollTimer = setTimeout(poll, 3000);
  }

  return { data, collectedAt, loaded, refreshing, error, load, refresh };
}
